import { TypographyVariants } from '../../Typography/types';
import { AvatarGroupLayoutProperties, AvatarGroupSize } from './types';

const BASE_AVATAR_GROUP_SIZE = 44;

type Config = Record<AvatarGroupSize, AvatarGroupLayoutProperties>;

export const AVATAR_GROUP_PROP_MAP: Config = {
  large: {
    borderSize: 4,
    label: 'label-01',
    margin: 18,
    position: 27,
    size: 56,
  },
  medium: {
    borderSize: 3,
    label: 'label-01',
    margin: 14,
    position: 22,
    size: 44,
  },
  small: {
    borderSize: 2,
    label: 'label-02',
    margin: 10,
    position: 16,
    size: 32,
  },
  'x-small': {
    borderSize: 2,
    label: 'label-03',
    margin: 8,
    position: 14,
    size: 28,
  },
  'xx-small': {
    borderSize: 1,
    label: 'body-short-03',
    margin: 8,
    position: 14,
    size: 20,
  },
};

export const DEFAULT_BORDER = 'border-solid border-bg-primary';

/**
 * Calculates avatar group configuration based on size
 * @param size - Either a predefined size string or a custom numeric size
 * @returns Configuration object with all necessary properties
 */
export const getAvatarGroupConfig = (size: AvatarGroupSize): AvatarGroupLayoutProperties => {
  // If it's a predefined size, return the mapped config
  if (typeof size === 'string') {
    return AVATAR_GROUP_PROP_MAP[size];
  }

  // For numeric sizes, calculate proportional values
  const numericSize = size;

  // If numeric size matches any preset size, return that preset config
  const presetMatch = (Object.values(AVATAR_GROUP_PROP_MAP) as AvatarGroupLayoutProperties[]).find(
    (cfg) => cfg.size === numericSize,
  );
  if (presetMatch) return presetMatch;

  // Base calculations using the medium size as reference (44px)
  const baseSize = BASE_AVATAR_GROUP_SIZE;
  const ratio = numericSize / baseSize;

  return {
    borderSize: Math.max(1, Math.round(3 * ratio)), // Minimum 1px border
    label: getTypographyVariant(numericSize),
    margin: Math.round(14 * ratio),
    position: Math.round(22 * ratio),
    size: numericSize,
  };
};

/**
 * Determines appropriate typography variant based on size
 * @param size - Numeric size value
 * @returns Typography variant string
 */
const getTypographyVariant = (size: number): TypographyVariants => {
  if (size >= 56) return 'label-01';
  if (size >= 44) return 'label-01';
  if (size >= 32) return 'label-02';
  if (size >= 28) return 'label-03';
  return 'body-short-03';
};
